import { useEffect, useState } from "react";

import apiClient from "../../../services/apiClient";
import Modal from "../../../components/Modal";
import StepProgressBar from "../../../components/StepProgressBar";
import CloseButton from "../../../components/CloseButton";
import ReviewSettingsPanel from "../../../components/ReviewSettingsPanel";
import { useDefaultRewardPoints } from "../../reward/RewardPointsField";
import DateSelectionPage from "./WorksheetModal/DateSelectionPage/DateSelectionPage";
import StudentSelectionPage from "./WorksheetModal/StudentSelectionPage/StudentSelectionPage";
import "../../../styles/WorksheetModalV2.css";

// 지인에듀 수학 학습지 출제 창(2026-10-06). 서버: api/assignment/math_views.py
// 영어 출제 창(WorksheetModal)과 같은 부품을 쓴다: 단계 바, 마감일·완료 포인트, 학생 선택, 복습 패널(무제한 포함).
// 수학은 지문·유형 대신 '문항' 단계에서 원본·쌍둥이와 태그(단원)를 고른다. 처음엔 전부 켜져 있고, 끈 것만 빠진다.
// 고를 것이 없는 학습지(출처 하나, 태그 하나 이하)는 이 단계를 건너뛰고 전부 낸다(2026-10-06: 빈 화면이 떴다).
const FULL_STEP_LIST = [
  { key: "title", label: "제목" },
  { key: "problem", label: "문항" },
  { key: "retry", label: "복습" },
  { key: "date", label: "마감일" },
  { key: "student", label: "학생" },
];

// 서버는 날짜만 받는다. toISOString 은 UTC 라 밤 9시 뒤에 고르면 하루 당겨진다
const toLocalDateText = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const MathAssignModal = ({ worksheet, onClose }) => {
  const [currentStep, setCurrentStep] = useState("title");
  const [title, setTitle] = useState(worksheet.title || "");
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });
  const [hideAfterClosed, setHideAfterClosed] = useState(true);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [rewardPoints, setRewardPoints] = useState(100);
  const [isRetryEnabled, setIsRetryEnabled] = useState(true);
  const [isAutoRetryEnabled, setIsAutoRetryEnabled] = useState(false);
  const [maxRetries, setMaxRetries] = useState(3);
  const [isSaving, setIsSaving] = useState(false);
  // 풀이 사진(2026-10-06): 유형 학습지는 선택, 나머지는 안 받음이 기본. 오답노트는 출제 창 없이 필수로 나간다
  const [workPhotoPolicy, setWorkPhotoPolicy] = useState(worksheet.type === "type_worksheet" ? "optional" : "");
  const [option, setOption] = useState(null);
  const [pickedOriginList, setPickedOriginList] = useState([]);
  const [pickedTagList, setPickedTagList] = useState([]);

  useEffect(() => {
    apiClient
      .get("/api/v1/assignment/math/worksheet", {
        params: { math_type: worksheet.type, math_id: worksheet.id },
        withAuth: true,
      })
      .then((response) => {
        setOption(response.data);
        setPickedOriginList(response.data.origin_list.map((one) => one.key));
        setPickedTagList(response.data.tag_list.map((one) => one.name));
      })
      .catch(() => setOption({ problem_count: 0, origin_list: [], tag_list: [], error: true }));
  }, [worksheet.type, worksheet.id]);

  const toggleIn = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((one) => one !== value) : [...list, value]);
  const [rewardMinScore, setRewardMinScore] = useState(0);
  useDefaultRewardPoints(`math_${worksheet.type}`, setRewardPoints, setRewardMinScore);

  const hasProblemChoice = Boolean(option && (option.origin_list.length > 1 || option.tag_list.length > 1));
  const STEP_LIST = hasProblemChoice ? FULL_STEP_LIST : FULL_STEP_LIST.filter((step) => step.key !== "problem");

  const goPrev = () => {
    const stepIndex = STEP_LIST.findIndex((step) => step.key === currentStep);
    if (stepIndex > 0) setCurrentStep(STEP_LIST[stepIndex - 1].key);
  };
  const handleStepClick = (stepKey) => {
    const targetIndex = STEP_LIST.findIndex((step) => step.key === stepKey);
    const currentIndex = STEP_LIST.findIndex((step) => step.key === currentStep);
    if (targetIndex < currentIndex) setCurrentStep(stepKey);
  };

  const submit = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const response = await apiClient.post(
        "/api/v1/assignment/math/create",
        {
          math_type: worksheet.type,
          math_id: worksheet.id,
          student_ids: selectedStudents.map((student) => student.id),
          title,
          due_date: toLocalDateText(selectedDate),
          reward_points: rewardPoints,
          reward_min_score: rewardMinScore,
          is_retry_enabled: isRetryEnabled,
          is_auto_retry_enabled: isAutoRetryEnabled,
          max_retries: maxRetries,
          hide_after_closed: hideAfterClosed,
          work_photo_policy: workPhotoPolicy,
          // 고를 것이 있었을 때만 거른다. 안 보내면 서버가 전부 낸다(목록을 못 받아 빈 채로 보내면 한 문제도 안 남는다)
          ...(hasProblemChoice ? { origin_list: pickedOriginList, tag_list: pickedTagList } : {}),
        },
        { withAuth: true }
      );
      alert(`${title} 과제가 ${response.data.assignment_count}명에게 출제되었습니다.`);
      onClose();
    } catch (error) {
      alert(error?.response?.data?.error || "과제 출제에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsSaving(false);
    }
  };

  const renderContent = () => {
    if (currentStep === "title") {
      const goNext = () => {
        if (!title.trim()) return alert("과제 제목을 입력해주세요.");
        setCurrentStep(hasProblemChoice ? "problem" : "retry");
      };
      return (
        <div style={{ position: "relative", width: "100%", display: "flex", flexDirection: "column" }}>
          <CloseButton onClick={onClose} />
          <div style={{ padding: "26px 22px 8px" }}>
            <div className="ws-modal-title">과제 제목 설정</div>
            <p className="ws-modal-sub" style={{ marginBottom: "18px" }}>
              학생 화면에 보여질 이름입니다.
            </p>
            <input
              type="text"
              placeholder="과제 제목을 입력해주세요."
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && goNext()}
              autoFocus
              style={{
                fontSize: "0.95rem",
                width: "100%",
                padding: "13px 16px",
                border: "1.5px solid #E6EDEC",
                borderRadius: "12px",
                boxSizing: "border-box",
                fontFamily: "inherit",
                outline: "none",
              }}
            />
          </div>
          <div className="ws-bottom-bar" style={{ marginTop: "26px" }}>
            <button className="ws-btn-next" onClick={goNext}>
              다음
            </button>
          </div>
        </div>
      );
    }
    if (currentStep === "problem") {
      return (
        <div style={{ position: "relative", width: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
          <CloseButton onClick={onClose} />
          <div className="ws-scroll" style={{ padding: "26px 22px 8px", overflowY: "auto" }}>
            <div className="ws-modal-title" style={{ marginBottom: "18px" }}>
              출제할 문항
            </div>
            {!option && <p className="ws-modal-sub">문항을 불러오는 중입니다.</p>}
            {option?.error && <p className="ws-modal-sub">학습지를 불러오지 못했습니다.</p>}
            {option && option.origin_list.length > 1 && (
              <div style={{ marginBottom: "18px" }}>
                <div style={problemLabelStyle}>원본 · 쌍둥이</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {option.origin_list.map((one) => (
                    <button
                      key={one.key}
                      type="button"
                      className={`ws-filter-chip${pickedOriginList.includes(one.key) ? " active" : ""}`}
                      onClick={() => toggleIn(pickedOriginList, setPickedOriginList, one.key)}
                    >
                      {one.name} {one.count}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {option && option.tag_list.length > 1 && (
              <div>
                <div style={problemLabelStyle}>태그</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {option.tag_list.map((one) => (
                    <button
                      key={one.name}
                      type="button"
                      className={`ws-filter-chip${pickedTagList.includes(one.name) ? " active" : ""}`}
                      onClick={() => toggleIn(pickedTagList, setPickedTagList, one.name)}
                    >
                      {one.name} {one.count}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="ws-bottom-bar" style={{ marginTop: "18px" }}>
            <button className="ws-btn-prev" onClick={goPrev}>
              이전
            </button>
            <button
              className="ws-btn-next"
              disabled={!option || option.error}
              onClick={() => {
                if (option.origin_list.length > 1 && pickedOriginList.length === 0)
                  return alert("원본이나 쌍둥이를 골라주세요.");
                if (option.tag_list.length > 1 && pickedTagList.length === 0) return alert("태그를 골라주세요.");
                setCurrentStep("retry");
              }}
            >
              다음
            </button>
          </div>
        </div>
      );
    }
    if (currentStep === "retry") {
      return (
        <div style={{ position: "relative", width: "100%", display: "flex", flexDirection: "column" }}>
          <CloseButton onClick={onClose} />
          <div style={{ padding: "26px 22px 8px" }}>
            <div className="ws-modal-title" style={{ marginBottom: "18px" }}>
              복습 설정
            </div>
            <ReviewSettingsPanel
              isRetryEnabled={isRetryEnabled}
              onToggleRetry={() => setIsRetryEnabled(!isRetryEnabled)}
              isAutoRetryEnabled={isAutoRetryEnabled}
              onSetAutoRetry={setIsAutoRetryEnabled}
              maxRetries={maxRetries}
              onSetMaxRetries={setMaxRetries}
            />
            <div style={{ marginTop: "16px" }}>
              <div style={problemLabelStyle}>풀이 사진</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {WORK_PHOTO_OPTION_LIST.map((one) => (
                  <button
                    key={one.value || "none"}
                    type="button"
                    className={`ws-filter-chip${workPhotoPolicy === one.value ? " active" : ""}`}
                    onClick={() => setWorkPhotoPolicy(one.value)}
                  >
                    {one.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="ws-bottom-bar" style={{ marginTop: "18px" }}>
            <button className="ws-btn-prev" onClick={goPrev}>
              이전
            </button>
            <button className="ws-btn-next" onClick={() => setCurrentStep("date")}>
              다음
            </button>
          </div>
        </div>
      );
    }
    if (currentStep === "date") {
      return (
        <DateSelectionPage
          onClose={onClose}
          onClickPrev={goPrev}
          onClickNext={() => setCurrentStep("student")}
          selectedDate={selectedDate}
          handleDateSelect={setSelectedDate}
          hideAfterClosed={hideAfterClosed}
          setHideAfterClosed={setHideAfterClosed}
          rewardPoints={rewardPoints}
          setRewardPoints={setRewardPoints}
          rewardMinScore={rewardMinScore}
          setRewardMinScore={setRewardMinScore}
        />
      );
    }
    return (
      <StudentSelectionPage
        onClose={onClose}
        onClickPrev={goPrev}
        handleAddStudents={submit}
        selectedStudents={selectedStudents}
        setSelectedStudents={setSelectedStudents}
        selectedDate={selectedDate}
      />
    );
  };

  return (
    <Modal>
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "min(560px, calc(100vw - 40px))",
          maxHeight: "min(calc(100vh - 40px), 80vh)",
          background: "#fff",
          borderRadius: "18px",
          overflow: "hidden",
          boxShadow: "0 10px 30px -22px rgba(20,40,50,.22)",
        }}
      >
        <StepProgressBar steps={STEP_LIST} currentStep={currentStep} onStepClick={handleStepClick} />
        {renderContent()}
      </div>
    </Modal>
  );
};

// 처음 풀 때 풀이 흔적을 사진으로 받는다(서버 api/assignment/math_work_photo.py)
const WORK_PHOTO_OPTION_LIST = [
  { value: "required", label: "필수" },
  { value: "optional", label: "선택" },
  { value: "", label: "안 받음" },
];

const problemLabelStyle = { fontSize: "0.8rem", fontWeight: 800, color: "#5B6670", marginBottom: "8px" };

export default MathAssignModal;
